import { useEffect, useState, useMemo, useCallback, memo } from "react";
import { Table, Button, Dropdown, Space, Pagination } from "antd";
import type { MenuProps, TableProps } from "antd";
import { FileTextOutlined, LinkOutlined, MoreOutlined } from "@ant-design/icons";
import IngestDocumentModal from "../../components/IngestDocumentModal/IngestDocumentModal";
import IngestURLModal from "../../components/IngestURLModal/IngestURLModal";
import RemoveDocumentModal from "../../components/RemoveDocumentModal/RemoveDocumentModal";
import RefreshDocumentModal from "../../components/RefreshDocumentModal/RefreshDocumentModal";
import ChunkModal from "../../components/ChunkModal/ChunkModal";
import ProcessModal from "../../components/ProcessModal/ProcessModal";
import MetadataModal from "../../components/MetadataModal/MetadataModal";
import SearchBar from "../../components/SearchBar/SearchBar";
import { fetchDocuments } from "../../api/api";

interface DocumentType {
  id: string;
  metadata?: { key: string; value: string }[];
  url: string;
  title: string;
  _ts: number;
  status: string;
  type: string;
  user: string;
  modified_at: string;
  modified_by: string;
}

interface TransformedDocument {
  key: string;
  id: string;
  title: string;
  dateModified: string;
  status: string;
  source: string;
  metadata?: { key: string; value: string }[];
  type: string;
  url: string;
  user: string;
}

const ExplorePage = memo(() => {
  const [showIngestURLModal, setShowIngestURLModal] = useState(false);
  const [showIngestDocumentModal, setShowIngestDocumentModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [showChunksModal, setShowChunksModal] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<TransformedDocument | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [documentCount, setDocumentCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [documents, setDocuments] = useState<TransformedDocument[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAllDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDocuments(
        currentPage,
        itemsPerPage,
        searchQuery,
        searchFilter
      );

      const transformedDocuments: TransformedDocument[] = data.items.map((doc: DocumentType) => {
        const metadataArray = Array.isArray(doc.metadata) ? doc.metadata : [];
        const titleMetadata = metadataArray.find(
          (m) => m.key?.toLowerCase() === "title"
        );
        const locationUrlMetadata = metadataArray.find((m: any) => m.key === "Location URL");

        return {
          key: doc.id,
          id: doc.id,
          title: doc.title || (titleMetadata ? titleMetadata.value : doc.url),
          dateModified: new Date(doc.modified_at).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }),
          status: doc.status,
          source: doc.url,
          metadata: Array.isArray(doc.metadata) ? doc.metadata : [],
          type: doc.type,
          url: locationUrlMetadata ? locationUrlMetadata.value : doc.url,
          user: doc.modified_by
        };
      });

      setDocuments(transformedDocuments);
      setDocumentCount(typeof data.total === "number" ? data.total : 0);
    } catch (error) {
      console.error("Failed to fetch all documents:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, searchFilter]);

  useEffect(() => {
    fetchAllDocuments();
  }, [fetchAllDocuments]);

  const handleSearch = useCallback((query: string, filter: string) => {
    setSearchQuery(query);
    setSearchFilter(filter);
    setCurrentPage(1);
  }, []);

  const handleReset = useCallback(() => {
    setSearchQuery("");
    setSearchFilter("");
    fetchAllDocuments();
  }, [fetchAllDocuments]);

  // Optimistic UI updates
  const handleOptimisticRemove = useCallback((documentId: string) => {
    setDocuments(prev => prev.filter(doc => doc.id !== documentId));
    setDocumentCount(prev => prev - 1);
  }, []);

  const handleOptimisticRefresh = useCallback((documentId: string) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === documentId 
        ? { ...doc, status: 'Processing...' }
        : doc
    ));
  }, []);

  const handleOptimisticProcess = useCallback((documentId: string) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === documentId 
        ? { ...doc, status: 'Processing...' }
        : doc
    ));
  }, []);

  const getActionMenuItems = useCallback((document: TransformedDocument): MenuProps['items'] => [
    {
      key: 'remove',
      label: 'Remove',
      onClick: () => {
        setSelectedDocument(document);
        setShowRemoveModal(true);
      },
    },
    {
      key: 'refresh',
      label: 'Refresh',
      onClick: () => {
        setSelectedDocument(document);
        setShowRefreshModal(true);
      },
    },
    {
      key: 'chunks',
      label: 'Chunks',
      onClick: () => {
        setSelectedDocument(document);
        setShowChunksModal(true);
      },
    },
    {
      key: 'metadata',
      label: 'Metadata',
      onClick: () => {
        setSelectedDocument(document);
        setShowMetadataModal(true);
      },
    },
    {
      key: 'process',
      label: 'Process',
      onClick: () => {
        setSelectedDocument(document);
        setShowProcessModal(true);
      },
    },
  ], []);

  const columns: TableProps<TransformedDocument>['columns'] = useMemo(() => [
    {
      title: '',
      dataIndex: 'type',
      key: 'type',
      width: 50,
      render: (type: string) =>
        type === "DOC" ? (
          <FileTextOutlined style={{ fontSize: 16 }} />
        ) : type === "URL" ? (
          <LinkOutlined style={{ fontSize: 16 }} />
        ) : null,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text: string, record) => (
        <a href={record.url} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      ),
    },
    {
      title: 'Date Modified',
      dataIndex: 'dateModified',
      key: 'dateModified',
      width: 150,
      render: (text: string, record) => (
        <>
          {text}
          <br />
          <span style={{ fontSize: 12, color: '#666' }}>{record.user}</span>
        </>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
    },
    {
      title: '',
      key: 'action',
      width: 120,
      render: (_: any, record) => (
        <Dropdown menu={{ items: getActionMenuItems(record) }} trigger={['click']}>
          <Button icon={<MoreOutlined />}>
            Manage
          </Button>
        </Dropdown>
      ),
    },
  ], [getActionMenuItems]);

  return (
    <div className="admin-content-container">
      <div className="page-header-row">
        <h2 className="page-title">Explore Documents</h2>
        <Space>
          <Button type="primary" onClick={() => setShowIngestDocumentModal(true)}>
            Ingest Document
          </Button>
          <Button type="primary" onClick={() => setShowIngestURLModal(true)}>
            Ingest URL
          </Button>
        </Space>
      </div>

      {/* Search Bar */}
      <SearchBar onSearch={handleSearch} onReset={handleReset} />

      {/* Documents Table */}
      <Table
        columns={columns}
        dataSource={documents}
        loading={loading}
        pagination={false}
        bordered
        size="small"
      />

      {/* Pagination */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
        <Pagination
          current={currentPage}
          total={documentCount}
          pageSize={itemsPerPage}
          onChange={setCurrentPage}
          showSizeChanger={false}
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
        />
      </div>

      {/* Modals */}
      <IngestDocumentModal
        show={showIngestDocumentModal}
        handleClose={() => {
          setShowIngestDocumentModal(false);
          fetchAllDocuments();
        }}
      />
      <IngestURLModal
        show={showIngestURLModal}
        handleClose={() => {
          setShowIngestURLModal(false);
          fetchAllDocuments();
        }}
      />

      {selectedDocument && (
        <>
          <ChunkModal
            show={showChunksModal}
            onHide={() => {
              setShowChunksModal(false);
              fetchAllDocuments();
            }}
            document={selectedDocument}
          />
          <RemoveDocumentModal
            show={showRemoveModal}
            onHide={() => {
              setShowRemoveModal(false);
              if (selectedDocument) {
                handleOptimisticRemove(selectedDocument.id);
              }
            }}
            document={selectedDocument}
          />
          <RefreshDocumentModal
            show={showRefreshModal}
            onHide={() => {
              setShowRefreshModal(false);
              if (selectedDocument) {
                handleOptimisticRefresh(selectedDocument.id);
              }
            }}
            document={selectedDocument}
          />
          <ProcessModal
            show={showProcessModal}
            onHide={() => {
              setShowProcessModal(false);
              if (selectedDocument) {
                handleOptimisticProcess(selectedDocument.id);
              }
            }}
            document={selectedDocument}
          />
          <MetadataModal
            show={showMetadataModal}
            onHide={() => {
              setShowMetadataModal(false);
              fetchAllDocuments();
            }}
            document={selectedDocument}
          />
        </>
      )}
    </div>
  );
});

ExplorePage.displayName = 'ExplorePage';

export default ExplorePage;
